import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
} from "react";
import axios from "axios";
import { AuthContext } from "./AuthContext";

const server_url = process.env.REACT_APP_BACKEND_URL;

interface TopScorer {
  user_id: number;
  team_id: number;
  name: string;
  goals: number;
}

interface SeasonTopScorers {
  season_id: number;
  top_scorers: TopScorer[];
}

interface User {
  id: number;
  name: string;
  mail: string | null;
  phone: string | null;
}

export interface Team {
  team_id: number;
  name: string;
  logo_url: string;
  name_short: string;
  is_active: boolean;
  created_at: string;
  captain: User | null;
  players: User[];
  player_count: number;
}

export interface TeamTable {
  id: number;
  name: string;
  matches: number;
  wins: number;
  draws: number;
  losses: number;
  goals_scored: number;
  goals_conceded: number;
}

export interface Scorer {
  id: number;
  user_id: number;
  team_id: number;
  goal_count: number;
}

export interface MatchResult {
  id: number;
  created_at: string;
  score_guest: number;
  score_home: number;
  submitted_by: number;
  submitted_by_user: User;
  is_final: boolean;
  match_id: number;
  scorer: Scorer[];
}

export interface Match {
  id: number;
  date: string;
  week: number;
  home_id: number;
  guest_id: number;
  season_id: number;
  match_stage: string;
  referee_team_id: number;
  referee_id: number;
  referee_team_id_2: number;
  referee_id_2: number;
  results: MatchResult[];
}

export interface Season {
  id: number;
  created_at: string;
  name: string;
  groups: Group[];
  matches: Match[];
}

export interface Group {
  id: number;
  created_at: string;
  name: string;
  season_id: number;
  teams: TeamTable[];
}

export interface BoardMember {
  name: string;
  role: string;
  mail: string;
  img_url: string;
  isActive?: boolean;
}

interface InfoContextType {
  isLoading: boolean;
  teams: Team[];
  seasons: Season[];
  boardMembers: BoardMember[];
  fetchTeams: () => Promise<void>;
  fetchSeasons: () => Promise<void>;
  fetchBoardMembers: () => Promise<void>;
  getTeamById: (teamId: number) => Team | undefined;
  getSeasonById: (seasonId: number) => Season | undefined;
  getPlayerByIdAndTeam: (teamId: number, playerId: number) => User | undefined;
  returnNLastMatches: (n: number) => Match[];
  topScorers: SeasonTopScorers[];
  fetchTopScorers: () => Promise<void>;
  getPenaltyLookup: (seasonId: number) => { [key: number]: number };
}

export const InfoContext = createContext<InfoContextType>({
  isLoading: false,
  teams: [],
  seasons: [],
  boardMembers: [],
  fetchTeams: async () => {},
  fetchSeasons: async () => {},
  fetchBoardMembers: async () => {},
  getTeamById: () => undefined,
  getSeasonById: () => undefined,
  getPlayerByIdAndTeam: () => undefined,
  returnNLastMatches: () => [],
  topScorers: [],
  fetchTopScorers: async () => {},
  getPenaltyLookup: () => ({}),
});

interface InfoProviderProps {
  children: ReactNode;
}

export const InfoProvider: React.FC<InfoProviderProps> = ({ children }) => {
  const { session } = useContext(AuthContext);
  const [teams, setTeams] = useState<Team[]>([]);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [boardMembers, setBoardMembers] = useState<BoardMember[]>([]);
  const [topScorers, setTopScorers] = useState<SeasonTopScorers[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [penaltyLookupMap, setPenaltyLookupMap] = useState<{
    [seasonId: number]: { [teamId: number]: number };
  }>({});

  useEffect(() => {
    fetchTeams();
  }, [session?.access_token]);

  const fetchSeasons = async () => {
    try {
      const response = await axios.get(`${server_url}/getSeasons`);
      if (response.data.status) {
        setSeasons(response.data.data);
      } else {
        console.error("Error fetching Seasons:", response.data.error);
      }
    } catch (err) {
      console.error("Error fetching Seasons:", err);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${server_url}/getGroups`);
      if (response.data.status) {
        setGroups(response.data.data);
      } else {
        console.error("Error fetching Groups:", response.data.error);
      }
    } catch (err) {
      console.error("Error fetching Groups:", err);
    }
  };

  const fetchBoardMembers = async () => {
    try {
      const response = await axios.get(`${server_url}/getBoardMembers`);
      if (response.data.status) {
        setBoardMembers(response.data.data);
      } else {
        console.error("Error fetching BoardMembers:", response.data.error);
      }
    } catch (err) {
      console.error("Error fetching BoardMembers:", err);
    }
  };

  const fetchTeams = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(`${server_url}/getTeams`, {
        access_token: session?.access_token,
      });
      if (response.data.status) {
        setTeams(response.data.data);
      } else {
        console.error("Error fetching teams:", response.data.error);
      }
    } catch (err) {
      console.error("Error fetching teams:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTopScorers = async () => {
    try {
      const response = await axios.get(`${server_url}/gettopscorer`);
      if (response.data.status) {
        setTopScorers(response.data.data);
      } else {
        console.error("Error fetching top scorers:", response.data.error);
      }
    } catch (err) {
      console.error("Error fetching top scorers:", err);
    }
  };

  const fetchPenaltyLookup = async (seasonId: number) => {
    try {
      const response = await axios.post(`${server_url}/getPenaltyDeductions`, {
        season_id: seasonId,
      });
      if (response.data.status) {
        setPenaltyLookupMap((prev) => ({
          ...prev,
          [seasonId]: response.data.data,
        }));
      }
    } catch (err) {
      console.error("Error fetching penalty deductions:", err);
    }
  };

  const getPenaltyLookup = (seasonId: number): { [key: number]: number } => {
    return penaltyLookupMap[seasonId] || {};
  };

  useEffect(() => {
    seasons.forEach((s) => {
      if (!penaltyLookupMap[s.id]) {
        fetchPenaltyLookup(s.id);
      }
    });
  }, [seasons]);

  const getTeamById = (teamId: number): Team | undefined => {
    return teams.find((team) => team.team_id === teamId);
  };

  const getSeasonById = (seasonId: number): Season | undefined => {
    return seasons.find((season) => season.id === seasonId);
  };

  const getPlayerByIdAndTeam = (
    teamId: number,
    playerId: number,
  ): User | undefined => {
    const team = teams.find((team) => team.team_id === teamId);
    if (!team) return undefined;
    if (team.captain?.id === playerId) {
      return team.captain;
    }
    return team.players.find((player) => player.id === playerId);
  };

  const returnNLastMatches = (n: number): Match[] => {
    if (!seasons.length) {
      return [];
    }

    const sortedSeasons = [...seasons].sort((a, b) => b.id - a.id);

    const collectedMatches: Match[] = [];

    for (const season of sortedSeasons) {
      const finalMatchesOfSeason = season.matches
        .filter((match) => match.results.some((res) => res.is_final))
        .sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        );

      for (const match of finalMatchesOfSeason) {
        if (collectedMatches.length < n) {
          collectedMatches.push(match);
        } else {
          break;
        }
      }

      if (collectedMatches.length >= n) {
        break;
      }
    }

    return collectedMatches;
  };

  useEffect(() => {
    fetchTeams();
    fetchGroups();
    fetchSeasons();
    fetchBoardMembers();
    fetchTopScorers();
  }, []);

  return (
    <InfoContext.Provider
      value={{
        isLoading,
        teams,
        seasons,
        boardMembers,
        fetchTeams,
        fetchSeasons,
        fetchBoardMembers,
        getTeamById,
        getSeasonById,
        getPlayerByIdAndTeam,
        returnNLastMatches,
        topScorers,
        fetchTopScorers,
        getPenaltyLookup,
      }}
    >
      {children}
    </InfoContext.Provider>
  );
};
